/**
 * Weapon mods — attach / detach, the reference-dependent type-fit rule and the SP budget.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type ApplyCtx, type Result } from "@lancer-vtt/shared";
import { MissingContentError } from "@lancer-vtt/compendium";
import type { AttachModCommand, DetachModCommand, RosterCommand } from "../commands";
import { licenseRequirement, meetsLicenseRequirement } from "../licenses";
import { checkLoadoutInvariants } from "../loadoutInvariants";
import { modFitsWeapon } from "../mounts";
import { meetsTalentRequirement, talentRequirement } from "../talentGear";
import type { RosterState, SlotData } from "../types";
import { requireComposedSlot, writeComposedSlot } from "./composedSlot";
import { changed, requireCompendium, requireLoadout, requireMech, requirePilot, withLoadout, withMech, withPilot, type RosterApplyResult } from "./context";

// ── attach_mod / detach_mod (reference-dependent: type-fit + SP-budget) ─────────

/**
 * Attach a weapon mod to an equipped weapon. Mirrors `applyEquipWeapon`'s shape and
 * enforces gates in order: (0) **`no_mods`** — the target mount's provenance
 * (`composedMountProvenance`) says its granting core bonus carries a structural
 * `{id:"no_mods", val:true}` row (`cb_integrated_weapon`) — hard-reject before anything else, targeted at that
 * mount only (a Flex-mount weapon, no `no_mods` row, is unaffected); (1) the slot
 * holds a weapon; (2) the slot doesn't already hold a mod (one-per-weapon); (3)
 * **type-fit** — the host weapon's `type` ∈ the mod's `allowed_types` (absent ⇒ all
 * allowed), via `modFitsWeapon`; (4) the **post-apply loadout invariants**
 * (`checkLoadoutInvariants`) — the SP-budget tenant (the mod's SP charged THROUGH
 * its host weapon must fit the mech's budget, the shared `sp.ts` helpers `compute.ts`
 * also reads) and the Unique tenant (no 2nd copy of a `tg_unique` mod across slots).
 * Reference-dependent — an unknown mod id is structured `MissingContentError`. The
 * inverse is a symmetric `detach_mod`.
 */
export function applyAttachMod(
  state: RosterState,
  cmd: AttachModCommand,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const loadoutR = requireLoadout(mech, cmd.loadoutId);
  if (!loadoutR.ok) return loadoutR;
  const loadout = loadoutR.value;
  const compendiumR = requireCompendium(ctx);
  if (!compendiumR.ok) return compendiumR;
  const compendium = compendiumR.value;

  // Address the COMPOSED array — same discipline as
  // `equip_weapon`/`unequip_weapon`.
  const resolvedR = requireComposedSlot({
    loadout,
    pilot,
    compendium,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    command: "attach_mod",
  });
  if (!resolvedR.ok) return resolvedR;
  const { composed, provenance, slot } = resolvedR.value;

  // Gate (0): `no_mods` — a bonus mount whose granting core bonus carries a
  // structural `{id:"no_mods", val:true}` row (`cb_integrated_weapon`'s RAW "It
  // can't be modified") hard-rejects ANY mod attach, before the other gates.
  // Read off `composedMountProvenance` for `cmd.mountIdx`, NOT a hardcoded
  // `cb_integrated_weapon` id check — targeted at the specific mount, so a weapon
  // in another bonus mount (e.g. the Flex mount, no `no_mods` row) is unaffected.
  const mountProvenance = provenance[cmd.mountIdx] ?? null;
  if (mountProvenance?.noMods) {
    return err(
      new CommandError(
        `attach_mod: mount ${cmd.mountIdx} was granted by ${mountProvenance.bonusId} with no_mods — its weapon can't be modified`,
      ),
    );
  }

  // Gate (1): the slot must hold a weapon — a mod rides a weapon, never an empty slot.
  if (slot.weaponId === null) {
    return err(
      new CommandError(
        `attach_mod: slot ${cmd.mountIdx}/${cmd.slotIdx} is empty; equip a weapon before modding it`,
      ),
    );
  }
  // Gate (2): one mod per weapon — reject a second mod on an already-modded slot.
  if (slot.modId !== null && slot.modId !== undefined) {
    return err(
      new CommandError(
        `attach_mod: slot ${cmd.mountIdx}/${cmd.slotIdx} already holds mod ${slot.modId}; detach it first`,
      ),
    );
  }

  // Resolve the mod (reference-dependent): an unknown id is structured missing
  // content, a `throw` (not the `Result` channel), like `equip_weapon`'s weapon.
  const mod = compendium.getById("mod", cmd.modId);
  if (!mod) {
    throw new MissingContentError("mod", cmd.modId, `mech ${cmd.mechId}`);
  }

  // License gate (single-source `licenses.ts` helper): hard-reject a mod whose
  // required license rank the pilot hasn't earned, alongside the type-fit + SP gates.
  // GMS mods pass via the seeded rank; unlicensed/rank-0 mods are always allowed.
  if (!meetsLicenseRequirement(pilot, mod, "mod")) {
    const req = licenseRequirement(mod, "mod");
    return err(
      new CommandError(
        `attach_mod: mod ${cmd.modId} requires license ${req?.id} rank ${req?.level}; pilot has not earned it`,
      ),
    );
  }

  // Talent gate (single-source `talentGear.ts` helper), for symmetry with the
  // weapon/system gates above. No mod in core carries `talent_id` today (dead
  // code — nothing observable exercises this branch yet), but keeps the three
  // equip gates uniform in case a future mod ever does. `ModSchema` isn't
  // widened with declared `talent_id`/`talent_rank` fields (unlike Weapon/
  // System) since no mod carries them; cast at the call site, the established
  // pattern for reading a passthrough-only field (see `weaponType`/`modFitsWeapon`
  // just below).
  const modTalent = mod as { talent_id?: string; talent_rank?: number };
  if (!meetsTalentRequirement(pilot, modTalent)) {
    const req = talentRequirement(modTalent);
    return err(
      new CommandError(
        `attach_mod: mod ${cmd.modId} requires talent ${req?.id} rank ${req?.rank}; pilot has not earned it`,
      ),
    );
  }

  // Gate (3): type-fit — the host weapon's `type` ∈ the mod's `allowed_types`.
  // `WeaponSchema.type` / `ModSchema.allowed_types` ride `.passthrough()`, so read
  // them via an `as` cast (the `equip_weapon` weapon-size precedent).
  const weapon = compendium.getById("weapon", slot.weaponId);
  if (!weapon) {
    throw new MissingContentError("weapon", slot.weaponId, `mech ${cmd.mechId}`);
  }
  const weaponType = (weapon as { type?: string }).type;
  const allowedTypes = (mod as { allowed_types?: string[] }).allowed_types;
  if (!modFitsWeapon(weaponType, allowedTypes)) {
    return err(
      new CommandError(
        `attach_mod: mod ${cmd.modId} (allowed ${allowedTypes?.join("|") ?? "any"}) does not fit weapon ${slot.weaponId} (type ${weaponType ?? "unknown"})`,
      ),
    );
  }

  const nextSlot: SlotData = { weaponId: slot.weaponId, modId: cmd.modId };
  const nextLoadout = writeComposedSlot({
    loadout,
    composed,
    provenance,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    slot: nextSlot,
  });
  const nextMech = withLoadout(mech, nextLoadout);

  // Gate (4): post-apply loadout invariants. The SP-budget tenant folds in the former
  // inline SP check (the mod's SP charged through its host weapon by `computeSpUsed`);
  // Unique bites here too — a 2nd copy of a `tg_unique` mod across slots.
  const violation = checkLoadoutInvariants(nextMech, pilot, compendium);
  if (violation) {
    return err(new CommandError(`attach_mod: ${violation.message}`));
  }

  const nextPilot = withMech(pilot, nextMech);
  return ok({
    state: withPilot(state, nextPilot),
    inverse: {
      type: "detach_mod",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      loadoutId: cmd.loadoutId,
      mountIdx: cmd.mountIdx,
      slotIdx: cmd.slotIdx,
    },
    events: changed(cmd.pilotId),
  });
}

/**
 * Detach a weapon mod from a slot (clear `modId`). The inverse of `attach_mod`; also
 * reachable directly. Raw-only in the sense that removal never overflows budget, but
 * now needs the compendium to address the COMPOSED array,
 * same as `unequip_weapon`. Its inverse re-attaches the cleared mod (or, if the slot
 * held no mod, detaches again — a no-op round-trip), symmetric, the `unequip_weapon`
 * model.
 */
export function applyDetachMod(
  state: RosterState,
  cmd: DetachModCommand,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const loadoutR = requireLoadout(mech, cmd.loadoutId);
  if (!loadoutR.ok) return loadoutR;
  const loadout = loadoutR.value;
  const compendiumR = requireCompendium(ctx);
  if (!compendiumR.ok) return compendiumR;
  const compendium = compendiumR.value;

  const resolvedR = requireComposedSlot({
    loadout,
    pilot,
    compendium,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    command: "detach_mod",
  });
  if (!resolvedR.ok) return resolvedR;
  const { composed, provenance, slot } = resolvedR.value;
  const prevModId = slot.modId ?? null;

  // Keep the weapon, clear only the mod. (`detach_mod` is unconditional even on a
  // `no_mods` mount — removing a mod never needs the gate that blocks attaching one.)
  const nextSlot: SlotData = { weaponId: slot.weaponId, modId: null };
  const nextLoadout = writeComposedSlot({
    loadout,
    composed,
    provenance,
    mountIdx: cmd.mountIdx,
    slotIdx: cmd.slotIdx,
    slot: nextSlot,
  });
  const nextMech = withLoadout(mech, nextLoadout);
  const nextPilot = withMech(pilot, nextMech);

  // Inverse: re-attach the mod we just cleared (restores it), or — if there was no
  // mod — detach again (a no-op round-trip). Symmetric, no lossy capture.
  const inverse: RosterCommand =
    prevModId === null
      ? {
          type: "detach_mod",
          actorId: cmd.actorId,
          pilotId: cmd.pilotId,
          mechId: cmd.mechId,
          loadoutId: cmd.loadoutId,
          mountIdx: cmd.mountIdx,
          slotIdx: cmd.slotIdx,
        }
      : {
          type: "attach_mod",
          actorId: cmd.actorId,
          pilotId: cmd.pilotId,
          mechId: cmd.mechId,
          loadoutId: cmd.loadoutId,
          mountIdx: cmd.mountIdx,
          slotIdx: cmd.slotIdx,
          modId: prevModId,
        };

  return ok({
    state: withPilot(state, nextPilot),
    inverse,
    events: changed(cmd.pilotId),
  });
}
